
import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationNext, PaginationPrevious } from '@/components/ui/pagination';
import { RefreshCw, Search, Database, Trash2, CheckCircle } from 'lucide-react';
import type { GdprDeletionRequest } from '@/services/gdpr/GdprDeletionService';

interface ActiveDeletionRequestsTableProps {
  deletionRequests: GdprDeletionRequest[];
  processing: string | null;
  onRefresh: () => void;
  onExecutePhase1: (userId: string) => void;
  onExecutePhase2: (userId: string) => void;
  getStatusColor: (status: string) => string;
}

export function ActiveDeletionRequestsTable({
  deletionRequests,
  processing,
  onRefresh,
  onExecutePhase1,
  onExecutePhase2,
  getStatusColor
}: ActiveDeletionRequestsTableProps) {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Filter and search logic
  const filteredRequests = useMemo(() => {
    return deletionRequests.filter(request => {
      const matchesSearch = !searchTerm || 
        request.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.userId.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [deletionRequests, searchTerm, statusFilter]);

  // Pagination logic
  const totalPages = Math.ceil(filteredRequests.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRequests = filteredRequests.slice(startIndex, endIndex);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, itemsPerPage]);

  const getUniqueStatuses = () => {
    const statuses = [...new Set(deletionRequests.map(req => req.status))];
    return statuses.sort();
  };

  return (
    <Card className="bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle className="text-gray-900 dark:text-gray-100">
            Active Deletion Requests ({filteredRequests.length})
          </CardTitle>
          <Button
            onClick={onRefresh}
            variant="outline"
            size="sm"
            className="border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
          >
            <RefreshCw className="h-4 w-4 mr-1" />
            Refresh
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Search and Filter Controls */}
        <div className="flex gap-4 items-end">
          <div className="flex-1">
            <Label htmlFor="search" className="text-gray-900 dark:text-gray-100">Search</Label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                id="search"
                placeholder="Search by email or user ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>
          
          <div className="w-48">
            <Label htmlFor="status-filter" className="text-gray-900 dark:text-gray-100">Status Filter</Label>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600">
                <SelectItem value="all">All Statuses</SelectItem>
                {getUniqueStatuses().map(status => (
                  <SelectItem key={status} value={status}>
                    {status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="w-32">
            <Label htmlFor="items-per-page" className="text-gray-900 dark:text-gray-100">Items per page</Label>
            <Select value={itemsPerPage.toString()} onValueChange={(value) => setItemsPerPage(Number(value))}>
              <SelectTrigger className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-gray-100">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600">
                <SelectItem value="5">5</SelectItem>
                <SelectItem value="10">10</SelectItem>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Results Table */}
        {filteredRequests.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-200 dark:border-gray-700">
            {searchTerm || statusFilter !== 'all' ? 'No requests match your filters' : 'No deletion requests found'}
          </div>
        ) : (
          <>
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50 dark:bg-gray-800/50">
                    <TableHead className="text-gray-900 dark:text-gray-100">Email</TableHead>
                    <TableHead className="text-gray-900 dark:text-gray-100">User ID</TableHead>
                    <TableHead className="text-gray-900 dark:text-gray-100">Status</TableHead>
                    <TableHead className="text-gray-900 dark:text-gray-100">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedRequests.map((request) => (
                    <TableRow key={request.id} className="bg-white dark:bg-gray-800/50">
                      <TableCell className="font-medium text-gray-900 dark:text-gray-100">
                        {request.email}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600 dark:text-gray-400 font-mono">
                        {request.userId}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${getStatusColor(request.status)} font-medium`}>
                          {request.status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {request.status === 'cooling_off' && (
                            <Button
                              size="sm"
                              onClick={() => onExecutePhase1(request.userId)}
                              disabled={processing === `phase1-${request.userId}`}
                              className="bg-blue-600 hover:bg-blue-700 text-white"
                            >
                              {processing === `phase1-${request.userId}` ? (
                                'Running Phase 1...'
                              ) : (
                                <>
                                  <Database className="h-4 w-4 mr-1" />
                                  Execute Phase 1
                                </>
                              )}
                            </Button>
                          )}

                          {request.status === 'pending_auth_deletion' && (
                            <Button
                              size="sm"
                              onClick={() => onExecutePhase2(request.userId)}
                              disabled={processing === `phase2-${request.userId}`}
                              className="bg-red-600 hover:bg-red-700 text-white"
                            >
                              {processing === `phase2-${request.userId}` ? (
                                'Running Phase 2...'
                              ) : (
                                <>
                                  <Trash2 className="h-4 w-4 mr-1" />
                                  Execute Phase 2
                                </>
                              )}
                            </Button>
                          )}

                          {request.status === 'completed' && (
                            <div className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                              <CheckCircle className="h-3 w-3" />
                              Deletion Complete
                            </div>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex justify-between items-center">
                <div className="text-sm text-gray-600 dark:text-gray-400">
                  Showing {startIndex + 1} to {Math.min(endIndex, filteredRequests.length)} of {filteredRequests.length} results
                </div>
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious 
                        onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                        className={currentPage === 1 ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                      />
                    </PaginationItem>
                    
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                      <PaginationItem key={page}>
                        <PaginationLink
                          onClick={() => setCurrentPage(page)}
                          isActive={currentPage === page}
                          className="cursor-pointer"
                        >
                          {page}
                        </PaginationLink>
                      </PaginationItem>
                    ))}
                    
                    <PaginationItem>
                      <PaginationNext 
                        onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                        className={currentPage === totalPages ? 'pointer-events-none opacity-50' : 'cursor-pointer'}
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
